import {
	Box,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Typography,
	Divider,
	LinearProgress,
} from "@mui/material";
import { formatDateWithTime } from "../../utils/dateUtils";
import { Avatar } from "../fieldsViews/fields/Avatar";
import { removeHtmlTagsAndEntitiesWithSpacesOnBlockElements } from "../../utils/removeHtmlTagsAndEntities";
import { EmojiPeople, Favorite, FolderShared, Groups, SupportAgent } from "@mui/icons-material";
import { useNoteTitleChoices } from "../../screens/treatments/case-management-notes/useNoteTitleChoices";
import { ColoredLabelChip } from "../chips/ColoredLabelChip";
import type { Note } from "../../types/records.types";
type NotesListProps = {
	selectedItem: number | null;
	setSelectedItem: (id: number | null) => void;
	notes: Array<Note>;
	notesStatus: "error" | "success" | "loading";
	renderSessionListView?: (item: any) => React.ReactNode | null;
};
export function NotesList({
	selectedItem,
	setSelectedItem,
	notes,
	notesStatus,
	renderSessionListView,
}: NotesListProps) {
	const titleChoices = useNoteTitleChoices();
	if (notesStatus === "loading") return <LinearProgress />;
	if (notesStatus === "error") return <div>error...</div>;
	return (
		<Box>
			<List sx={{ width: "100%" }}>
				{notes.map((item: Note) => {
					const shortString = item.content?.substring(0, 100);
					const plainText = removeHtmlTagsAndEntitiesWithSpacesOnBlockElements(shortString);
					const titleColor = titleChoices.find((choice) => choice.value === item.title)?.color;
					return (
						<Box key={item.id}>
							<ListItem
								alignItems="flex-start"
								sx={{
									cursor: "pointer",
									backgroundColor:
										selectedItem === item.id ? "grey.200" : "transparent",
								}}
								onClick={() => {
									setSelectedItem(item.id);
								}}
							>
								<ListItemAvatar>
									<Avatar value={item?.user_created?.avatar} />
								</ListItemAvatar>
								<ListItemText
									primary={
										plainText.length > 50
											? `${plainText.substring(0, 50)}...`
											: plainText
									}
									secondaryTypographyProps={{ component: "div" }}
											secondary={
												<Box sx={{ transform: "scale(0.9)", transformOrigin: "left center", display: "flex", flexDirection: "column", gap: 0.5 }}>
													<Box display="flex" alignItems="center" gap={1}>
														<EntitiesIcons item={item} iconColor={titleColor} />
														<Typography
															sx={{ display: "inline", fontSize: "0.8rem" }}
															component="span"
															color="text.primary"
														>
															{formatDateWithTime(item.date_created)}
														</Typography>
														<NoteTitleChip title={item.title} titleChoices={titleChoices} />
													</Box>
													{renderSessionListView?.(item)}
												</Box>
											}
								/>
							</ListItem>
							<Divider variant="fullWidth" component="li" />
						</Box>
					);
				})}
			</List>
		</Box>
	);
}
type EntitiesIconsProps = {
	item: Note;
	iconColor?: string;
};
const EntitiesIcons = ({ item, iconColor }: EntitiesIconsProps) => {
	const colorProps = getIconColorProps(iconColor);
	if (item.leads) return <EmojiPeople {...colorProps} />;
	if (item.treatments) {
		return getNoteIcon(item?.category, iconColor)
	}
	if (item.clients) return <Groups {...colorProps} />;
	return null;
};
export const getNoteIcon = (category?: string | null, iconColor?: string) => {
	const colorProps = getIconColorProps(iconColor);
	if (category === "client_owner") return <FolderShared {...colorProps} />;
	if (category === "psychology") return <Favorite {...colorProps} />;
	return <SupportAgent {...colorProps} />;
};
const getIconColorProps = (iconColor?: string) => {
	const colorProps = iconColor ? { sx: { color: iconColor } } : { color: "secondary" as const };
	return colorProps;
};
type NoteTitleChipProps = {
	title?: string | null;
	titleChoices: Array<{ value: string; text: string; color?: string }>;
};
const NoteTitleChip = ({ title, titleChoices }: NoteTitleChipProps) => {
	const titleChoice = titleChoices.find((c) => c.value === title);
	if (!titleChoice?.color) return null;
	return <ColoredLabelChip label={titleChoice.text} color={titleChoice.color} size="small" />;
};
